import { type FormEvent, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { LogIn, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/api/client';

export function Login() {
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromRaw = searchParams.get('from');
  const from = fromRaw ? decodeURIComponent(fromRaw) : '/';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already logged in? Bounce straight in.
  if (!authLoading && user) {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Invalid username or password.');
      } else {
        setError('Something went wrong. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-bg px-4">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-lg bg-kam-grad shadow-glow-violet" />
          <div>
            <div className="text-base font-bold tracking-tight">KAM</div>
            <div className="text-[10px] text-text-faint uppercase tracking-[0.14em]">
              Korean Asset Manager
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="card">
          <h1 className="font-semibold mb-1">Sign in</h1>
          <p className="text-xs text-text-faint mb-5">Operator access only.</p>

          <label className="block mb-3">
            <span className="label block mb-1.5">Username</span>
            <input
              type="text"
              autoComplete="username"
              autoFocus
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 rounded-[10px] bg-surface-2 border border-[color:var(--border)] text-text text-sm focus:outline-none focus:border-kam-violet/50"
            />
          </label>

          <label className="block mb-4">
            <span className="label block mb-1.5">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded-[10px] bg-surface-2 border border-[color:var(--border)] text-text text-sm focus:outline-none focus:border-kam-violet/50"
            />
          </label>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 mb-4 rounded-[10px] bg-kam-red-soft text-kam-red border border-kam-red/25 text-xs">
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-[10px] bg-kam-grad text-white font-semibold text-sm shadow-glow-violet disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-[11px] text-text-faint mt-6">
          Paper trading dashboard · KRX
        </p>
      </div>
    </div>
  );
}
